import {
    AfterViewInit, Component, Input, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChildren
} from '@angular/core';
import { AxisLabelContentArgs, ChartComponent, SeriesLabelsContentArgs } from '@progress/kendo-angular-charts';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UtilityService } from '../../services/shared/utility.service';
import { BcmDashboardService } from '../../services/bcm-dashboard.service';
import {
    BcmBreakdowns,
    BcmCategoryRow,
    BcmChartPoint,
    BcmCharts,
    BcmClubRollup,
    BcmFilter,
    BcmPeriod,
    BcmSummary,
    createDefaultBcmFilter
} from '../../models/bcm-dashboard.model';

export enum HostTimeFrame {
    ThisMonth = 1,
    LastMonth = 2,
    Custom = 3
}

const TOTAL_SECTIONS = 3;

const SERIES_COLOR = '#2a78d6';

const GRID_COLOR = '#e5ebf4';

const AREA_OPACITY = 0.12;

const LABEL_ALL_UP_TO_LINE = 18;
const LABEL_ALL_UP_TO_BARS = 12;
const BAR_LABEL_FULL_UP_TO = 8;

interface CategoryLabelsView {
    font: string;
    color: string;
    padding: { top: number };
    rotation: number;
    step: number;
}

const MARKERS_UP_TO = 12;

// Most category labels to draw on a month axis before thinning kicks in.
const MAX_AXIS_LABELS = 12;

const MARKERS_ON = Object.freeze({
    visible: true, size: 8, background: SERIES_COLOR,

    border: Object.freeze({ width: 2, color: '#ffffff' })
});
const MARKERS_OFF = Object.freeze({ visible: false });

interface ValueAxisView {
    majorUnit: number;
    labels: object;
    majorGridLines: object;
    minorGridLines: object;
    majorTicks: object;
    line: object;
}

interface CategoryRowView {
    key: string;
    label: string;
    count: string;
}

interface ChartView {
    categories: string[];
    values: number[];
}

@Component({
    selector: 'app-rpt-bcm-dashboard',
    templateUrl: './rpt-bcm-dashboard.component.html',

    styleUrls: ['./rpt-bcm-dashboard.component.css']
})
export class RptBcmDashboardComponent implements OnChanges, AfterViewInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    @ViewChildren(ChartComponent) charts: QueryList<ChartComponent>;

    @Input() encryptedUser = '';

    @Input() teamCode: number = 0;

    @Input() timeFrame: number = HostTimeFrame.LastMonth;

    @Input() customStart: string = '';
    @Input() customEnd: string = '';

    filter: BcmFilter = createDefaultBcmFilter();

    loading = false;
    loadedSections = 0;
    loadProgress = 0;
    readonly totalSections = TOTAL_SECTIONS;

    errorMsg = '';

    periodName = '';
    periodLabel = '';

    spillNoticeVisible = false;

    contractsValue = '';
    feesValue = '';

    byAgent: CategoryRowView[] = [];
    byStatus: CategoryRowView[] = [];
    bySite: CategoryRowView[] = [];

    collectedTotal = '';
    collectedContracts = '';
    collectedAverage = '';

    monthlyFees: ChartView = { categories: [], values: [] };
    totalSales: ChartView = { categories: [], values: [] };

    totalSalesSubtitle = '';

    readonly seriesColor = SERIES_COLOR;
    readonly areaOpacity = AREA_OPACITY;
    readonly chartArea = { background: 'transparent', margin: { top: 30, right: 34, bottom: 14, left: 24 } };

    markers: object = MARKERS_OFF;

    monthlyGap = 8;

    readonly categoryAxisStyle = {
        majorGridLines: { visible: false },
        majorTicks: { visible: false },
        line: { color: GRID_COLOR },

        /* Set explicitly, because Kendo's own default depends on the series type and that is
           what caused the "$60K$55K" collision on Total Sales.
           From kendo-charts/chart/plotarea/categorical-plotarea.js:

               isJustified() { for (...) if (!inArray(series.type, AREA_SERIES)) return false;
                               return true; }
               if (!defined(axisOptions.justified)) axisOptions.justified = this.isJustified();

           So an all-area chart justifies by default: its first point sits exactly ON the value
           axis, and a data label centred on that point spills left over the axis labels. The
           column chart never showed it because a column series returns false there.
           `defined(...)` is the escape hatch - naming the value ourselves stops Kendo deciding
           it from the series type, so the two charts behave the same and stay that way if
           either series type ever changes. */
        justified: false
    };

    // Each chart gets its own label config because the two axes can hold different numbers of
    // categories - Monthly Fees counts months with fees, Total Sales spans the whole window.
    monthlyAxisLabels: CategoryLabelsView = RptBcmDashboardComponent.axisLabels(0);
    salesAxisLabels: CategoryLabelsView = RptBcmDashboardComponent.axisLabels(0);

    private static readonly VALUE_AXIS_BASE = {
        majorGridLines: { color: GRID_COLOR, width: 1 },
        minorGridLines: { visible: false },
        majorTicks: { visible: false },
        line: { visible: false }
    };

    monthlyValueAxis: ValueAxisView = RptBcmDashboardComponent.valueAxis(0);
    salesValueAxis: ValueAxisView = RptBcmDashboardComponent.valueAxis(0);

    private static niceStep(max: number): number {
        if (!(max > 0)) { return 1; }

        const target = max / 4;
        const magnitude = Math.pow(10, Math.floor(Math.log10(target)));
        const normalised = target / magnitude;
        const multiplier = normalised <= 1 ? 1
            : normalised <= 2 ? 2
            : normalised <= 2.5 ? 2.5
            : normalised <= 5 ? 5
            : 10;

        return multiplier * magnitude;
    }

    private static valueAxis(max: number): ValueAxisView {
        const step = RptBcmDashboardComponent.niceStep(max);

        const candidates: number[] = [];
        if (max >= 1000000) { candidates.push(1000000); }
        if (max >= 10000) { candidates.push(1000); }
        candidates.push(1);

        let divisor = 1;
        let decimals = -1;

        for (const unit of candidates) {
            const per = step / unit;
            if (per >= 1 && Math.abs(per - Math.round(per)) < 1e-9) { divisor = unit; decimals = 0; break; }
            if (per >= 0.1 && Math.abs(per * 10 - Math.round(per * 10)) < 1e-9) { divisor = unit; decimals = 1; break; }
        }

        if (decimals < 0) {
            divisor = 1;
            decimals = Math.min(2, RptBcmDashboardComponent.decimalsOf(step));
        }

        const suffix = divisor === 1000000 ? 'M' : (divisor === 1000 ? 'K' : '');

        return {
            majorUnit: step,
            labels: {
                font: '12px Inter, Segoe UI, Arial, sans-serif', color: '#94a3b8',
                /* Belt and braces with `justified: false` above. That insets the first point by
                   half a category slot, which is ample at 5 points but only ~15px at 32, and a
                   "$55K" label reaches about 20px either side of its point. This padding pulls
                   the axis labels away from the plot instead, so the clearance does not depend
                   on how many months are in range. */
                padding: { right: 8 },
                content: (e: AxisLabelContentArgs) =>
                    '$' + RptBcmDashboardComponent.grouped(Number(e.value) / divisor, decimals) + suffix
            },
            ...RptBcmDashboardComponent.VALUE_AXIS_BASE
        };
    }

    private static readonly VALUE_AXIS_TITLE = Object.freeze({
        text: 'Fees',
        font: '12px Inter, Segoe UI, Arial, sans-serif',
        color: '#64748b',
        margin: Object.freeze({ right: 4 })
    });

    readonly valueAxisTitle: object = RptBcmDashboardComponent.VALUE_AXIS_TITLE;

    private static decimalsOf(value: number): number {
        for (let d = 0; d <= 4; d++) {
            const scaled = value * Math.pow(10, d);
            if (Math.abs(scaled - Math.round(scaled)) < 1e-9) { return d; }
        }
        return 4;
    }

    private monthlyLabelled = new Set<string>();
    private salesLabelled = new Set<string>();
    private monthlyLabelsAbbreviated = false;

    readonly monthlySeriesLabels = {
        visible: true, font: '600 12px Inter, Segoe UI, Arial, sans-serif', color: '#334155',
        position: 'outsideEnd', background: 'transparent', padding: { bottom: 2 },
        content: (e: SeriesLabelsContentArgs) => {
            if (!this.monthlyLabelled.has(String(e.category))) { return ''; }
            return this.monthlyLabelsAbbreviated
                ? RptBcmDashboardComponent.thousands(Number(e.value))
                : RptBcmDashboardComponent.money(Number(e.value), 0);
        }
    };

    readonly salesSeriesLabels = {
        visible: true, font: '600 12px Inter, Segoe UI, Arial, sans-serif', color: '#334155',
        position: 'above', background: 'transparent',
        content: (e: SeriesLabelsContentArgs) =>
            this.salesLabelled.has(String(e.category))
                ? RptBcmDashboardComponent.thousands(Number(e.value))
                : ''
    };

    constructor(
        private readonly bcmService: BcmDashboardService,
        private readonly utilityService: UtilityService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.encryptedUser) { return; }

        const watched = ['encryptedUser', 'teamCode', 'timeFrame', 'customStart', 'customEnd'];
        if (!watched.some(name => changes[name])) { return; }

        if (this.timeFrame === HostTimeFrame.Custom && !(this.customStart && this.customEnd)) { return; }

        this.apply();
    }

    ngAfterViewInit(): void {
        [0, 400, 1500].forEach(ms => setTimeout(() => this.redraw(), ms));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    apply(): void {
        this.filter = this.buildFilter();
        this.errorMsg = '';
        this.loading = true;
        this.loadedSections = 0;
        this.loadProgress = 0;

        const f = this.filter;

        this.load(this.bcmService.getSummary(this.encryptedUser, f), res => this.applySummary(res.summary));
        this.load(this.bcmService.getBreakdowns(this.encryptedUser, f), res => this.applyBreakdowns(res.breakdowns));
        this.load(this.bcmService.getCharts(this.encryptedUser, f), res => this.applyCharts(res.charts));
    }

    private buildFilter(): BcmFilter {
        const filter = createDefaultBcmFilter();

        filter.teamCodes = this.teamCode > 0 ? [Number(this.teamCode)] : [];

        if (Number(this.timeFrame) === HostTimeFrame.Custom) {
            filter.useCustomRange = true;
            filter.fromDate = RptBcmDashboardComponent.fromHostDate(this.customStart);
            filter.toDate = RptBcmDashboardComponent.fromHostDate(this.customEnd);
            return filter;
        }

        filter.useCustomRange = false;
        filter.period = Number(this.timeFrame) === HostTimeFrame.LastMonth
            ? BcmPeriod.LastMonth
            : BcmPeriod.ThisMonth;

        return filter;
    }

    private static fromHostDate(value: string): string {
        if (!value || !/^\d{8}$/.test(value)) { return null; }
        return value.substring(4, 8) + '-' + value.substring(0, 2) + '-' + value.substring(2, 4);
    }

    private load<T>(obs: Observable<T>, assign: (res: T) => void): void {
        obs.pipe(takeUntil(this.destroy$)).subscribe(
            (res: T) => {
                this.captureError(res);
                assign(res);
                this.settle();
            },
            (err: HttpErrorResponse) => {
                this.errorMsg = err && err.message
                    ? 'The report could not be reached: ' + err.message
                    : 'The report could not be reached.';
                this.settle();
                this.utilityService.handleErrorResponse(err);
            });
    }

    private captureError(res: unknown): void {
        const envelope = res as { statusCode?: number; errorMsg?: string };
        if (!envelope) {
            this.errorMsg = 'The report returned an empty response.';
            return;
        }
        if (envelope.errorMsg) {
            this.errorMsg = envelope.errorMsg;
            console.error('[bcm-dashboard] server reported:', envelope.statusCode, envelope.errorMsg);
        } else if (envelope.statusCode && envelope.statusCode >= 400) {
            this.errorMsg = 'The report returned status ' + envelope.statusCode + '.';
            console.error('[bcm-dashboard] server status', envelope.statusCode);
        }
    }

    private settle(): void {
        this.loadedSections++;
        this.loadProgress = Math.min(100, Math.round((this.loadedSections / TOTAL_SECTIONS) * 100));
        if (this.loadedSections >= TOTAL_SECTIONS) { this.loading = false; }
        setTimeout(() => this.redraw(), 0);
    }

    private redraw(): void {
        if (this.charts) { this.charts.forEach(c => { try { c.resize(); } catch {  } }); }
    }

    private applySummary(summary: BcmSummary): void {
        const s = summary || ({} as BcmSummary);
        this.applyPeriod(s);

        this.contractsValue = RptBcmDashboardComponent.grouped(s.contracts || 0, 0);
        this.feesValue = RptBcmDashboardComponent.money(s.feesCollected || 0, 2);
    }

    private applyBreakdowns(breakdowns: BcmBreakdowns): void {
        const b = breakdowns || ({} as BcmBreakdowns);
        this.applyPeriod(b);

        this.byAgent = RptBcmDashboardComponent.rows(b.byAgent, 'a');
        this.byStatus = RptBcmDashboardComponent.rows(b.byStatus, 's');
        this.bySite = RptBcmDashboardComponent.rows(b.bySite, 'i');

        const cf = b.collectedFees || { total: 0, contracts: 0, average: 0 };
        this.collectedTotal = RptBcmDashboardComponent.money(cf.total || 0, 2);
        this.collectedContracts = RptBcmDashboardComponent.grouped(cf.contracts || 0, 0);
        this.collectedAverage = RptBcmDashboardComponent.money(cf.average || 0, 2);
    }

    private applyCharts(charts: BcmCharts): void {
        const c = charts || ({} as BcmCharts);
        this.applyPeriod(c);

        const monthly = c.monthlyFees || [];
        const sales = c.totalSales || [];

        this.monthlyFees = { categories: monthly.map(p => p.label), values: monthly.map(p => p.value) };
        this.totalSales = { categories: sales.map(p => p.label), values: sales.map(p => p.value) };
        this.totalSalesSubtitle = c.totalSalesGrain === 'day'
            ? (this.periodLabel || 'One point per day in the selected range, up to today')
            : 'One point per month across the selected range';

        this.monthlyLabelled = RptBcmDashboardComponent.labelled(monthly, LABEL_ALL_UP_TO_BARS);
        this.salesLabelled = RptBcmDashboardComponent.labelled(sales, LABEL_ALL_UP_TO_LINE);

        this.monthlyLabelsAbbreviated = monthly.length > BAR_LABEL_FULL_UP_TO;

        this.markers = sales.length <= MARKERS_UP_TO ? MARKERS_ON : MARKERS_OFF;

        this.monthlyGap = RptBcmDashboardComponent.gapFor(monthly.length);
        this.monthlyAxisLabels = RptBcmDashboardComponent.axisLabels(monthly.length);
        this.salesAxisLabels = RptBcmDashboardComponent.axisLabels(sales.length);

        this.monthlyValueAxis = RptBcmDashboardComponent.valueAxis(
            RptBcmDashboardComponent.maxOf(this.monthlyFees.values));
        this.salesValueAxis = RptBcmDashboardComponent.valueAxis(
            RptBcmDashboardComponent.maxOf(this.totalSales.values));
    }

    private static maxOf(values: number[]): number {
        let max = 0;
        (values || []).forEach(v => { if (v > max) { max = v; } });
        return max;
    }

    private static labelled(points: BcmChartPoint[], maxLabels: number): Set<string> {
        const set = new Set<string>();
        if (!points.length) { return set; }

        const step = points.length <= maxLabels
            ? 1
            : Math.ceil(points.length / maxLabels);

        for (let i = 0; i < points.length; i += step) {
            /* A ZERO POINT GETS NO DIRECT LABEL. At day grain most days took no fees, and this loop
               would otherwise print "$0K" over every one of them - a row of labels that costs ink,
               crowds out the values that matter and makes an ordinary quiet week look like an error.
               The max and the endpoint below are added regardless, so a series that is zero all the
               way through still keeps exactly one label and still says which day it ends on. */
            if (points[i].value !== 0) { set.add(points[i].label); }
        }

        let max = points[0];
        points.forEach(p => { if (p.value > max.value) { max = p; } });
        set.add(max.label);

        let endpoint = points[points.length - 1];
        for (let i = points.length - 1; i >= 0; i--) {
            if (points[i].value !== 0) { endpoint = points[i]; break; }
        }
        set.add(endpoint.label);

        return set;
    }

    /* Diagonal labels, and only as many of them as actually fit.
       ROTATION alone does not solve this. At 32 months in a ~500px plot each category owns
       about 15px, while "Sep 2026" at 12px is roughly 55px wide - 39px of horizontal footprint
       once turned 45 degrees. Still overlapping. So the labels are also thinned with `step`,
       which tells Kendo to render every nth category: aiming for at most MAX_AXIS_LABELS keeps
       roughly 45px between them, which a 45-degree label clears.
       Below 5 categories there is nothing to avoid and a lone tilted label just looks broken,
       so those stay horizontal. */
    private static axisLabels(count: number): CategoryLabelsView {
        return {
            font: '12px Inter, Segoe UI, Arial, sans-serif',
            color: '#64748b',
            padding: { top: 6 },
            rotation: count > 4 ? -45 : 0,
            step: count > MAX_AXIS_LABELS ? Math.ceil(count / MAX_AXIS_LABELS) : 1
        };
    }

    private static gapFor(count: number): number {
        if (count <= 1) { return 10; }
        if (count === 2) { return 6; }
        if (count <= 4) { return 3; }
        if (count <= 8) { return 0.9; }
        return 0.7;
    }

    private applyPeriod(section: { period?: { name: string; label: string; end: string; isCustomRange: boolean } }): void {
        const p = section ? section.period : null;
        if (!p) { return; }

        this.periodName = p.name || '';
        this.periodLabel = p.label || '';

        const end = RptBcmDashboardComponent.toDate(p.end);
        this.spillNoticeVisible = !p.isCustomRange
            && p.name === 'Last Month'
            && !!end
            && end.getDate() > 1;
    }

    private static rows(source: BcmCategoryRow[], prefix: string): CategoryRowView[] {
        return (source || []).map((r, i) => ({
            key: prefix + i,

            label: r.label || '',
            count: RptBcmDashboardComponent.grouped(r.contracts, 0)
        }));
    }

    private static readonly FORMATS: { [key: string]: Intl.NumberFormat } = {};

    private static formatter(key: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
        let f = RptBcmDashboardComponent.FORMATS[key];
        if (!f) {
            f = new Intl.NumberFormat('en-US', options);
            RptBcmDashboardComponent.FORMATS[key] = f;
        }
        return f;
    }

    private static grouped(value: number, decimals: number): string {
        return RptBcmDashboardComponent.formatter('n' + decimals, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            useGrouping: true
        }).format(value || 0);
    }

    private static money(value: number, decimals: number): string {
        return RptBcmDashboardComponent.formatter('c' + decimals, {
            style: 'currency', currency: 'USD',
            minimumFractionDigits: decimals, maximumFractionDigits: decimals
        }).format(value || 0);
    }

    private static thousands(value: number): string {
        const v = (value || 0) / 1000;
        return '$' + RptBcmDashboardComponent.formatter('n0', {
            minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true
        }).format(v) + 'K';
    }

    private static millions(value: number): string {
        const v = (value || 0) / 1000000;
        return '$' + RptBcmDashboardComponent.formatter('n1', {
            minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: true
        }).format(v) + 'M';
    }

    private static toDate(iso: string): Date {
        if (!iso) { return null; }
        const datePart = iso.length >= 10 ? iso.substring(0, 10) : iso;
        const parts = datePart.split('-');
        if (parts.length !== 3) { return null; }
        const y = Number(parts[0]), m = Number(parts[1]), d = Number(parts[2]);
        if (!y || !m || !d) { return null; }
        return new Date(y, m - 1, d);
    }

    private static toIso(value: Date): string {
        if (!value) { return null; }
        const m = value.getMonth() + 1;
        const d = value.getDate();
        return value.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (d < 10 ? '0' + d : d);
    }

    trackByKey(_index: number, row: CategoryRowView): string {
        return row ? row.key : String(_index);
    }
}
